/**
 * @file   src\Pages\PaxRevenueReports.tsx
 * @brief  Pax Revenue Report page for each carwash (revenue report without User column, Bay Type instead of Wash Type).
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { Container, Table, Breadcrumb, useNavigate, useEffect, useOutletContext, useState, Button, useParams, Pagination, React } from '../utils/thirdpartyComponents';
import { userType, serviceType } from '../utils/enums';
import { pageURLs } from '../utils/pageUrls';
import { AppContextType } from '../interfaces/generalInterface';
import DatePicker from 'react-datepicker';
import CWSelect from '../components/CWSelect';
import axios from '../config/axiosConfig';
import { CompanyApi, RevenueApi } from '../utils/apiUrls';
import SmallLoader from '../components/SmallLoader';
import { MessageToaster } from '../utils/toastUtil';
import { MONTHS_OPTIONS } from '../utils/constants';
import moment from "moment-timezone"

interface PaxRevenueRecord {
  LocationName: string;
  WashName: string;
  BayName: string;
  BayType: number;
  TransactionID: string;
  InvoiceId: string;
  TransactionDate: string;
  TimeOfVisit?: string;
  Revenue: number;
}

const PaxRevenueReports = (props: any) => {

  // Access the context
  const { updateData } = useOutletContext<AppContextType>();
  const carwash_owner = (props.userType === userType['CARWASH-OWNER']) ? true : false

  const navigate = useNavigate()

  const params = useParams();
  const today = new Date();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [washRecords, setWashRecords] = useState<any[]>([]);
  const [hasRunReport, setHasRunReport] = useState(false);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [filters, setFilters] = useState({
    month: '',
    year: '',
    bayType: '',
    fromDate: '',
    toDate: '',
  });
  const toaster = new MessageToaster();
  const timezoneOffset = new Date().getTimezoneOffset();
  const pageSize = 10;

  const bayTypeOptions = [
    { label: 'All', value: 'all' },
    { label: 'In Bay/Tunnel', value: serviceType.Inbay.toString() },
    { label: 'Self Serve', value: serviceType.SelfServe.toString() },
    { label: 'Dog Wash', value: serviceType.DogWash.toString() },
    { label: 'Vacuum', value: serviceType.Vacuum.toString() },
  ];

  const yearOptions = Array.from(
    { length: new Date().getFullYear() - 2024 + 1 },
    (_, i) => {
      const year = 2024 + i;
      return { label: year.toString(), value: year.toString() };
    }
  );
  const selectedYearOption = yearOptions.find((option) => option.value === filters.year);
  const selectedMonthOption = MONTHS_OPTIONS.find((option) => option.value === filters.month);

  // State to company details
  const [company, setCompany] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: ""
  });

  // To display heading
  useEffect(() => {
    getCompanyAddress();
  }, [])

  useEffect(() => {
    handleUpdate()
  }, [company])

  useEffect(() => {
    if (hasRunReport && filters.month && filters.year) {
      getRecords(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  const getDateRange = () => {
    if (!filters.month || !filters.year) {
      return { minDate: null, maxDate: null };
    }
    const year = parseInt(filters.year);
    const month = parseInt(filters.month) - 1;
    const minDate = new Date(year, month, 1);
    const maxDate = new Date(year, month + 1, 0);
    return { minDate, maxDate };
  };

  const handleFilterChange = (name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setHasRunReport(false);
  };

  const handleFromDateChange = (date: Date | null) => {
    setFromDate(date);
    setHasRunReport(false);
    if (date) {
      setFilters((prev) => ({ ...prev, fromDate: moment(date).format('YYYY-MM-DD') }));
    } else {
      setFilters((prev) => ({ ...prev, fromDate: '' }));
    }
  };

  const handleToDateChange = (date: Date | null) => {
    setToDate(date);
    setHasRunReport(false);
    if (date) {
      setFilters((prev) => ({ ...prev, toDate: moment(date).format('YYYY-MM-DD') }));
    } else {
      setFilters((prev) => ({ ...prev, toDate: '' }));
    }
  };

  const getCompanyAddress = async () => {
    try {
      const id = params.id ? parseInt(params.id) : 0;
      const { data } = (carwash_owner) ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS) : await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id)
      if (data?.error === 0) {
        setCompany(data.data);
      }

    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  }

  // Update the data
  const handleUpdate = () => {
    updateData({ heading: "Pax Revenue Report", url: carwash_owner ? "" : `/superadmin/${pageURLs.carwash_locations}/${params.id}`, showBackButton: props.userType === userType['CARWASH-OWNER'] ? false : true, showMainMenu: true, activeLink: props.userType === userType['CARWASH-OWNER'] ? "reports" : "manage-car-wash" });
  };



  const fetchPaxRevenueData = async (pageNo: number, pageSizeParam: number) => {
    if (filters.month === '' || filters.year === '') {
      return null;
    }

    const queryParams = new URLSearchParams();

    queryParams.append('offset', timezoneOffset.toString());
    queryParams.append('pageNo', pageNo.toString());
    queryParams.append('pageSize', pageSizeParam.toString());

    if (params.id) {
      queryParams.append('carWashId', params.id.toString());
    }

    if (filters.bayType && filters.bayType !== 'all') queryParams.append('bayType', filters.bayType);
    if (filters.month) queryParams.append('month', filters.month);
    if (filters.year) queryParams.append('year', filters.year);
    if (filters.fromDate) queryParams.append('fromDate', filters.fromDate);
    if (filters.toDate) queryParams.append('toDate', filters.toDate);

    const { data } = await axios.get(`${RevenueApi.CARWASH_OWNER_PAX_REVENUE_REPORT}?${queryParams.toString()}`);

    if (data?.error === 0 && Array.isArray(data?.data?.list)) {
      const normalizedList: PaxRevenueRecord[] = data.data.list.map((item: any) => ({
        LocationName: item?.LocationName ?? item?.locationName ?? '-',
        WashName: item?.WashName ?? item?.washName ?? '-',
        BayName: item?.BayName ?? item?.bayName ?? '-',
        BayType: Number(item?.BayType ?? item?.SubscriptionType ?? 0),
        TransactionID: item?.TransactionID ?? item?.transactionID ?? item?.transactionId ?? '-',
        InvoiceId: item?.InvoiceId ?? item?.invoiceId ?? '-',
        TransactionDate: item?.TransactionDate ?? item?.createdOn ?? '',
        TimeOfVisit: item?.TimeOfVisit ?? item?.VisitTime ?? item?.VisitDateTime ?? '',
        Revenue: Number(item?.Revenue ?? item?.Total ?? item?.Amount ?? 0),
      }));

      return {
        list: normalizedList,
        totalCount: Number(data.data.totalCount ?? normalizedList.length),
        limit: Number(data.data.limit ?? pageSizeParam ?? pageSize),
      };
    }

    return null;
  };

  const getRecords = async (pageNo = 1) => {
    try {
      setLoading(true);
      const result = await fetchPaxRevenueData(pageNo, pageSize);

      if (result?.list?.length) {
        setWashRecords(result.list);
        const resolvedLimit = Number(result.limit) > 0 ? Number(result.limit) : pageSize;
        setTotalPages(Math.ceil(Number(result.totalCount || 0) / resolvedLimit) || 1);
      } else {
        setWashRecords([]);
        setTotalPages(1);
      }
    } catch (error: any) {
      console.error('Error fetching pax revenue report:', error.message);
      toaster.toastError(error.response?.data?.message || error.message || 'Failed to fetch report');
      setWashRecords([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const itemClick = (url: string) => {
    navigate(url);
  };


  const getVisiblePageNumbers = () => {
    const totalPageNumbersToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(totalPageNumbersToShow / 2));
    let endPage = startPage + totalPageNumbersToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getBayTypeLabel = (bayType: number) => {
    const bayTypeMap: { [key: number]: string } = {
      [serviceType.Inbay]: 'In Bay/Tunnel',
      [serviceType.SelfServe]: 'Self Serve',
      [serviceType.DogWash]: 'Dog Wash',
      [serviceType.Vacuum]: 'Vacuum',
    };
    return bayTypeMap[bayType] || 'Unknown';
  };

  const formatDate = (transactionDate: string) => {
    if (!transactionDate) return '-';
    return moment.utc(transactionDate).format('MM/DD/YYYY');
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return '-';
    return moment(timeString, ['HH:mm:ss', 'HH:mm']).format('h:mm A');
  };

  return (

    <div className='manage-carwash-container order-list-container multi-location-wrap'>
      <Container>
        <Breadcrumb>
          {carwash_owner ?
            <>
              <Breadcrumb.Item onClick={() => itemClick(`/${pageURLs.dashboard}`)}>Dashboard</Breadcrumb.Item>
            </> :
            <>
              <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_carwash}`) }}>Manage Car Washes</Breadcrumb.Item>
              <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.carwash_locations}/${params.id}`) }} >{company.companyName}</Breadcrumb.Item>
            </>

          }
          <Breadcrumb.Item active>Pax Revenue Report</Breadcrumb.Item>
        </Breadcrumb>
        <div className="manage-header-block d-flex justify-content-between align-items-center">
          <div className="search-wrap multi-location-action w-100">
            <div className="result-group d-flex align-items-center">
              <CWSelect
                id="selectyear"
                name="selectyear"
                options={yearOptions}
                values={selectedYearOption ? [selectedYearOption] : []}
                label="Select Year"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                onChange={(option: any) => {
                  const selectedYear = option[0]?.value?.toString() || '';
                  setFilters((prev) => ({ ...prev, year: selectedYear, fromDate: '', toDate: '' }));
                  setHasRunReport(false);
                  setFromDate(null);
                  setToDate(null);
                }}
              />
              <CWSelect
                id="selectmonth"
                name="selectmonth"
                options={MONTHS_OPTIONS}
                values={selectedMonthOption ? [selectedMonthOption] : []}
                label="Select Month"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                onChange={(option: any) => {
                  const selectedMonth = option[0]?.value?.toString() || '';
                  setFilters((prev) => ({ ...prev, month: selectedMonth, fromDate: '', toDate: '' }));
                  setHasRunReport(false);
                  setFromDate(null);
                  setToDate(null);
                }}
              />
              <div className="d-flex flex-column">
                <label className="form-label">Date Range</label>
                <div className="d-flex align-items-center">
                  {(() => {
                    const { minDate, maxDate } = getDateRange();
                    const isDatePickerDisabled = !filters.month || !filters.year;
                    return (
                      <>
                        <DatePicker
                          showIcon
                          selected={fromDate}
                          onChange={handleFromDateChange}
                          placeholderText="From Date"
                          disabled={isDatePickerDisabled}
                          minDate={minDate || undefined}
                          maxDate={maxDate || undefined}
                          openToDate={fromDate || minDate || today}
                          dateFormat="MM/dd/yyyy"
                        />
                        <div className="ms-3">
                          <DatePicker
                            showIcon
                            selected={toDate}
                            onChange={handleToDateChange}
                            placeholderText="To Date"
                            disabled={isDatePickerDisabled}
                            minDate={fromDate || minDate || undefined}
                            maxDate={maxDate || undefined}
                            openToDate={toDate || fromDate || minDate || today}
                            dateFormat="MM/dd/yyyy"
                          />
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
              <CWSelect
                id="baytype"
                name="baytype"
                options={bayTypeOptions}
                label="Bay Type"
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                onChange={(option: any) =>
                  handleFilterChange('bayType', option[0]?.value?.toString() || '')
                }
              />
              <div className="button-group d-flex">
                <Button
                  className="primary-btn"
                  variant="primary"
                  title="Run Report"
                  onClick={() => {
                    if (filters.month && filters.year) {
                      setHasRunReport(true);
                      if (currentPage !== 1) {
                        setCurrentPage(1);
                      } else {
                        getRecords(1);
                      }
                    } else {
                      toaster.toastError('Please select month and year before running report.');
                    }
                  }}
                >
                  Run Report
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="cw-table-wrap report-table">
          <Table responsive className='m-0'>
            <thead>
              <tr>
                <th>Car Wash Location</th>
                <th>Wash Name</th>
                <th>Bay Name</th>
                <th>Bay Type</th>
                <th>Transaction ID</th>
                <th>Invoice ID</th>
                <th>Date</th>
                <th>Time of Visit</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {(washRecords).length > 0 ?
                (washRecords).map((record, index) => (
                  <tr key={index}>
                    <td>{record.LocationName ?? '-'}</td>
                    <td>{record.WashName ?? '-'}</td>
                    <td>{record.BayName ?? '-'}</td>
                    <td>
                      {getBayTypeLabel(
                        Number(record.BayType ?? 0)
                      )}
                    </td>
                    <td>{record.TransactionID ?? '-'}</td>
                    <td>{record.InvoiceId ?? '-'}</td>
                    <td>{formatDate(record.TransactionDate ?? '')}</td>
                    <td>{formatTime(record.TimeOfVisit ?? '')}</td>
                    <td>${Number(record.Revenue ?? 0).toFixed(2)}</td>
                  </tr>
                )) :
                (
                  <tr>
                    <td style={{ textAlign: 'center' }} colSpan={9} >No data found.</td>
                  </tr>
                )}
            </tbody>
          </Table>

          {washRecords.length ? <div className="mt-3 d-flex justify-content-end">
            <Pagination>
              <Pagination.First
                disabled={currentPage === 1}
                onClick={() => handlePageChange(1)}
              />
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              />
              {getVisiblePageNumbers().map((pageNumber) => (
                <Pagination.Item
                  key={pageNumber}
                  active={pageNumber === currentPage}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              />
              <Pagination.Last
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(totalPages)}
              />
            </Pagination>
          </div> : null}
        </div>
      </Container >

      {loading && <SmallLoader />}
    </div >
  )
}

export default PaxRevenueReports;
